// M1-1: scene service — fetches task data via the 3DGS share API and converts
// it to the Explorer-internal SceneConfig.

import type {
  SceneConfig,
  SceneSplatEntry,
  TaskData,
  AccessInfo,
  FetchSceneResult,
  PlyWebpYupJson,
} from '@app-types/scene';
import type { ExperienceSettings } from '@app-types/experience';
import {
  Colmap,
  Vec3,
  smartTrackFromColmap,
  type ColmapData,
  type Track,
} from '@mindcloudxai_3d/core';
import { resolveDirectUrlHotspots, resolveSceneHotspots } from '@modules/hotspots/data';
import { mapExperienceSettings } from './experienceMapper';
import { http } from '@utils/http';
import { createLogger } from '@utils/logger';

const log = createLogger('scene-service');

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface FetchSceneOptions {
  taskCode?: string;
  taskId?: number;
  signal?: AbortSignal;
}

export interface ResolveDefaultSceneTrackOptions {
  signal?: AbortSignal;
}

// ---------------------------------------------------------------------------
// 后端响应结构
// ---------------------------------------------------------------------------

/** /task/visit 响应 */
interface TaskVisitResponse {
  task: TaskData;
  access?: { user_id?: number; role?: string; can_download?: boolean; can_edit_share?: boolean };
  owner?: unknown;
  editor?: unknown;
}

// ---------------------------------------------------------------------------
// fetchScene — 统一场景数据获取
// task_code → /task/visit（主链路），task_id → /task/get（快速路径）
// ---------------------------------------------------------------------------

export async function fetchScene(options: FetchSceneOptions): Promise<FetchSceneResult> {
  log.info('fetchScene', { taskCode: options.taskCode, taskId: options.taskId });

  // 主链路：task_code → /task/visit（含 access.role 权限信息）
  if (options.taskCode) {
    const { data } = await http.post<TaskVisitResponse>(
      '/api/v1/task/visit',
      { task_code: options.taskCode },
      { signal: options.signal },
    );
    // data 已有 TaskVisitResponse 类型，直接按结构取值；normalizeTaskDetail 兜底异形响应
    const task = normalizeTaskDetail(data.task ?? data);
    const access = mapAccessInfo(data.access);
    log.debug('fetchScene via task/visit', { task, access });
    return { task, access };
  }

  // 快速路径：task_id → /task/get（owner 内部入口，无 access 信息）
  if (!options.taskId) {
    throw new Error('Missing task identifier (need taskCode or taskId)');
  }
  const { data } = await http.post<TaskData>(
    '/api/v1/task/get',
    { id: options.taskId },
    { signal: options.signal },
  );
  log.debug('fetchScene via task/get', data);
  // /task/get 仅 owner/admin 可调用，缺省填充 owner 权限以正确驱动分享等 UI
  return {
    task: normalizeTaskDetail(data),
    access: { role: 'owner', canEditShare: true, canDownload: true },
  };
}

/** 将后端 access 对象映射为 Explorer 内部 AccessInfo */
function mapAccessInfo(raw?: {
  role?: string;
  can_download?: boolean;
  can_edit_share?: boolean;
}): AccessInfo | undefined {
  if (!raw?.role) return undefined;
  return {
    role: (raw.role as AccessInfo['role']) || 'visitor',
    canEditShare: raw.can_edit_share ?? false,
    canDownload: raw.can_download ?? false,
  };
}

// ---------------------------------------------------------------------------
// Conversion: TaskData → SceneConfig
// ---------------------------------------------------------------------------

/**
 * Convert raw backend TaskData into the Explorer-internal SceneConfig.
 *
 * Fields that the backend doesn't yet provide (hotspots, cinematic track,
 * initial camera) are filled with sensible defaults and marked TODO.
 */
export function toSceneConfig(task: TaskData): SceneConfig {
  const splatUrl = resolveSplatUrl(task);
  const experience = resolveExperienceSettings(task);
  const mapped = experience ? mapExperienceSettings(experience) : null;
  const voxel = task.output_result?.voxel;

  return {
    id: task.task_code,
    name: task.task_name || task.project_name || task.task_code,
    description: undefined,
    creator: task.user_name ? { id: String(task.creator), name: task.user_name } : undefined,
    asset: {
      id: task.task_code,
      url: splatUrl,
      format: resolveSplatFormat(task),
      byteSize:
        task.output_result?.lod_meta?.size ||
        task.output_result?.ply_webp?.size ||
        task.output_result?.splat?.size ||
        undefined,
      sogMeta: resolveSogMeta(task),
    },
    initialCamera: mapped?.initialCamera ?? {
      position: new Vec3(0, 1.5, 4),
      target: Vec3.ZERO,
      fov: 60,
    },
    // experience annotations 优先；否则走原有 hotspot 解析
    hotspots: mapped?.hotspots.length ? mapped.hotspots : resolveSceneHotspots(task),
    track: mapped?.track ?? undefined,
    experienceSettings: experience ?? undefined,
    collisionUrl: voxel?.url || undefined,
    collisionBinUrl: voxel?.metadata?.bin_url || undefined,
  };
}

export function toDirectSceneConfig(url: string): SceneConfig {
  return {
    id: 'direct-url',
    name: 'Direct scene',
    asset: {
      id: 'direct-url',
      url,
      format: resolveFormatFromUrl(url),
    },
    initialCamera: {
      position: new Vec3(0, 1.5, 4),
      target: Vec3.ZERO,
      fov: 60,
    },
    hotspots: resolveDirectUrlHotspots('direct-url'),
    track: undefined,
  };
}

export async function resolveDefaultSceneTrack(
  task: TaskData,
  options: ResolveDefaultSceneTrackOptions = {},
): Promise<Track | null> {
  const colmap = await loadTaskColmap(task, options.signal);
  if (!colmap) return null;
  // transform.ts 内部完成坐标系转换，smartTrackFromColmap 直接输出展示空间坐标。
  return smartTrackFromColmap(colmap, { name: 'colmap-default' });
}

function normalizeTaskDetail(payload: unknown): TaskData {
  if (isTaskData(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>;
    const candidate = p.task ?? p.content ?? p.detail ?? p.data;
    if (isTaskData(candidate)) return candidate;
  }
  throw new Error('Invalid task detail response');
}

function isTaskData(value: unknown): value is TaskData {
  return !!value && typeof value === 'object' && 'task_code' in value;
}

/**
 * 从 TaskData 各候选位置提取 ExperienceSettings。
 * 优先级：metadata.experience_settings > extra_fields.experience_settings > model_config.experience_settings
 */
function resolveExperienceSettings(task: TaskData): ExperienceSettings | null {
  const candidates: unknown[] = [
    task.metadata?.experience_settings,
    task.extra_fields?.experience_settings,
    task.model_config?.experience_settings,
  ];

  for (const raw of candidates) {
    const parsed = typeof raw === 'string' ? safeJsonParse(raw) : raw;
    if (parsed && typeof parsed === 'object' && 'version' in parsed) {
      const settings = parsed as ExperienceSettings;
      if (settings.version === 2) {
        log.info('resolveExperienceSettings: found version 2');
        return settings;
      }
    }
  }
  return null;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/**
 * 业务层只负责取数：优先 colmap_path JSON，回退 sparse 文本；
 * 结构化解析统一交给 shared 层 Colmap 工厂。
 */
async function loadTaskColmap(task: TaskData, signal?: AbortSignal): Promise<Colmap | null> {
  const colmapPath = resolveColmapPath(task);
  if (colmapPath) {
    try {
      return Colmap.fromJson(await fetchJson<ColmapData>(colmapPath, signal));
    } catch (err) {
      if (isAbortError(err)) throw err;
      log.warn('loadTaskColmap: failed to load colmap_path', err);
    }
  }

  const sparse = task.sparse;
  if (!sparse?.images && !sparse?.cameras && !sparse?.points3D) return null;

  try {
    const [imagesText, camerasText, points3DText] = await Promise.all([
      sparse.images ? fetchText(sparse.images, signal) : Promise.resolve(undefined),
      sparse.cameras ? fetchText(sparse.cameras, signal) : Promise.resolve(undefined),
      sparse.points3D ? fetchText(sparse.points3D, signal) : Promise.resolve(undefined),
    ]);
    return Colmap.fromSparseText({ imagesText, camerasText, points3DText });
  } catch (err) {
    if (isAbortError(err)) throw err;
    log.warn('loadTaskColmap: failed to load sparse files', err);
    return null;
  }
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

async function fetchText(url: string, signal?: AbortSignal): Promise<string> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status}`);
  }
  return response.text();
}

function resolveColmapPath(task: TaskData): string | null {
  const fromExtraFields = readString(task.extra_fields?.colmap_path);
  if (fromExtraFields) return fromExtraFields;

  const parsedExtraFields =
    typeof task.extra_fields_str === 'string' ? safeJsonParse(task.extra_fields_str) : null;
  if (parsedExtraFields && typeof parsedExtraFields === 'object') {
    return readString((parsedExtraFields as Record<string, unknown>).colmap_path);
  }

  return null;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

/**
 * Infer the splat format from whichever output_result source was chosen.
 * Mirrors the priority in `resolveSplatUrl`.
 */
function resolveSplatFormat(task: TaskData): SceneSplatEntry['format'] {
  const out = task.output_result;
  if (out?.lod_meta?.url) return 'lod-meta';
  if (out?.ply_webp?.url) return 'sog';
  if (out?.splat?.url) return 'splat';
  return 'splat';
}

/**
 * SOG 时提取内联 JSON 描述符，有值时 viewer 优先使用而非从 url 下载。
 * is_yup=true → ply_webp_yup_json；否则 ply_webp_json。
 *
 * 仅在最终选用 SOG 资源时才返回：sogMeta 会被 viewer 优先于 url 使用，
 * 若 lod_meta 已被选为资源源（见 resolveSplatUrl/resolveSplatFormat），
 * 返回 sogMeta 会让 viewer 改走 SOG 而忽略默认 LOD 数据。
 */
function resolveSogMeta(task: TaskData): PlyWebpYupJson | undefined {
  const out = task.output_result;
  if (out?.lod_meta?.url) return undefined;
  const pw = out?.ply_webp;
  if (!pw) return undefined;
  const json = task.is_yup
    ? (pw.ply_webp_yup_json ?? pw.ply_webp_json)
    : (pw.ply_webp_json ?? pw.ply_webp_yup_json);
  return json ?? undefined;
}

function resolveFormatFromUrl(url: string): SceneSplatEntry['format'] {
  const path = url.split('?')[0]?.toLowerCase() ?? '';
  if (path.endsWith('lod-meta.json')) return 'lod-meta';
  if (path.endsWith('.compressed-splat')) return 'compressed-splat';
  return 'splat';
}

/**
 * Try multiple sources for the splat URL.
 *
 * Priority:
 * 1. `output_result.lod_meta.url` — PlayCanvas LOD metadata
 * 2. `output_result.ply_webp.url` — SOG JSON descriptor
 * 3. `output_result.splat.url`    — compact splat binary
 */
function resolveSplatUrl(task: TaskData): string {
  const out = task.output_result;
  if (out) {
    if (out.lod_meta?.url) {
      log.info('resolveSplatUrl: using output_result.lod_meta.url');
      return out.lod_meta.url;
    }
    if (out.ply_webp?.url) {
      log.info('resolveSplatUrl: using output_result.ply_webp.url (sog)');
      return out.ply_webp.url;
    }
    if (out.splat?.url) {
      log.info('resolveSplatUrl: using output_result.splat.url');
      return out.splat.url;
    }
  }
  return '';
}
